import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Bolt } from "@mui/icons-material";
import { listWorkflows, STATUS_COLOR, WorkflowRow } from "../api/workflows";
import { JobRow, listJobs } from "../api/jobs";
import { formatTs } from "../api/workflowSummary";
import { extractErrorMessage } from "../api/client";

const POLL_MS = 5000;

// "Cluster activity" — live view of every k8s Job spawning on the
// cluster, whether it's part of a multi-stage workflow (batch_export,
// batch_onboard, etc.) or a standalone single Job spawned by another
// path (archive refresh → scan, search-env provision → index_search_env,
// admin rm-indices, etc.). The rule is: anything that puts a pod on
// the cluster shows up here. The detail of why a Job exists lives on
// whichever page submitted it (workflow detail / search-env detail /
// admin page) — this surface is the "what's running" inventory.
//
// Two parallel polls (5s each) for workflows + standalone jobs, then
// merged into one ``ActivityRow`` list. Standalone-jobs are filtered
// down to those WITHOUT a workflow_id (the workflow-bound ones are
// already represented by their parent workflow row).

interface ActivityRow {
  id: string;
  kind: string;
  status: string;
  submitted_by: string | null;
  submitted_at: number;
  finished_at: number | null;
  // /exports/<id> for workflows, /search-envs/<env_id> for
  // index_search_env jobs (parsed out of params_json), undefined for
  // standalone scan / rm / etc. Drives whether the id renders as
  // a Link or plain text.
  link: string | null;
  // "workflow" or "job" — used only for the small badge that tells
  // the user this is a standalone Job rather than a multi-stage
  // Workflow. Removed if it gets noisy.
  source: "workflow" | "job";
}

function workflowToRow(wf: WorkflowRow): ActivityRow {
  return {
    id: wf.id,
    kind: wf.kind,
    status: wf.status,
    submitted_by: wf.submitted_by,
    submitted_at: wf.submitted_at,
    finished_at: wf.finished_at,
    link: `/exports/${wf.id}`,
    source: "workflow",
  };
}

function jobToRow(job: JobRow): ActivityRow {
  // For index_search_env jobs we know there's a corresponding
  // search-env page — peel the env id out of params_json so the
  // user can click through. Failures fall back to plain text.
  let link: string | null = null;
  if (job.kind === "index_search_env") {
    try {
      // params_json isn't in the typed schema today; safe-cast for
      // the parse. Drops to null if the operator didn't include it.
      const raw = (job as unknown as { params_json?: string }).params_json;
      if (raw) {
        const params = JSON.parse(raw);
        if (typeof params.search_env_id === "string") {
          link = `/search-envs/${params.search_env_id}`;
        }
      }
    } catch {
      // ignore — no link
    }
  }
  return {
    id: job.id,
    kind: job.kind,
    status: job.status,
    submitted_by: job.submitted_by,
    submitted_at: job.submitted_at,
    finished_at: job.finished_at,
    link,
    source: "job",
  };
}

function sortRows(rows: ActivityRow[]): ActivityRow[] {
  return [...rows].sort((a, b) => {
    const aRun = a.status === "running" ? 0 : 1;
    const bRun = b.status === "running" ? 0 : 1;
    if (aRun !== bRun) return aRun - bRun;
    return b.submitted_at - a.submitted_at;
  });
}

export function ClusterActivity() {
  const [workflows, setWorkflows] = useState<WorkflowRow[] | null>(null);
  const [standaloneJobs, setStandaloneJobs] = useState<JobRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const [wfs, js] = await Promise.all([
          listWorkflows({ limit: 200 }),
          listJobs({ limit: 200 }),
        ]);
        if (cancelled) return;
        setWorkflows(wfs.workflows);
        // Only keep jobs without a workflow_id — workflow-bound jobs
        // are already represented by their parent workflow row.
        setStandaloneJobs(js.jobs.filter((j) => !j.workflow_id));
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(extractErrorMessage(err));
      }
      if (!cancelled) setTimeout(tick, POLL_MS);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows: ActivityRow[] | null =
    workflows === null || standaloneJobs === null
      ? null
      : sortRows([
          ...workflows.map(workflowToRow),
          ...standaloneJobs.map(jobToRow),
        ]);

  const runningCount = rows?.filter((r) => r.status === "running").length ?? 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" gap={2} mb={3}>
        <Bolt fontSize="large" color="primary" />
        <Box>
          <Typography variant="h4">Cluster activity</Typography>
          <Typography variant="caption" color="text.secondary">
            Live view of every workflow + standalone Job on the cluster — exports,
            indexing, rebuilds, archive scans, search-env indexers.
            {rows !== null && ` ${runningCount} running, ${rows.length} total.`}
          </Typography>
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!rows ? (
        <CircularProgress size={20} />
      ) : rows.length === 0 ? (
        <Alert severity="info">No activity yet.</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>id</TableCell>
              <TableCell>kind</TableCell>
              <TableCell>status</TableCell>
              <TableCell>submitted by</TableCell>
              <TableCell>submitted at</TableCell>
              <TableCell>finished at</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={`${r.source}:${r.id}`} hover>
                <TableCell>
                  {r.link ? (
                    <Link component={RouterLink} to={r.link}>
                      <code>{r.id}</code>
                    </Link>
                  ) : (
                    <code>{r.id}</code>
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip size="small" variant="outlined" label={r.kind} />
                    {r.source === "job" && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="job"
                        sx={{ opacity: 0.6, fontSize: "0.7rem", height: 18 }}
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={r.status}
                    color={
                      STATUS_COLOR[r.status as keyof typeof STATUS_COLOR] ??
                      "default"
                    }
                  />
                </TableCell>
                <TableCell>{r.submitted_by ?? "—"}</TableCell>
                <TableCell>{formatTs(r.submitted_at)}</TableCell>
                <TableCell>{r.finished_at ? formatTs(r.finished_at) : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Container>
  );
}
